<script lang="ts" setup>
const props = defineProps<{
  opened: boolean;
}>();

const { template, coupleName } = storeToRefs(useTemplateStore());
const { md } = useBreakpointTailwind();

const formattedDate = computed(() => {
  const date = template.value.general.date;

  if (!date) {
    return "";
  }

  const eventDate = new Date(date);
  const day = String(eventDate.getDate()).padStart(2, "0");
  const month = String(eventDate.getMonth() + 1).padStart(2, "0");
  const year = eventDate.getFullYear();

  return `${day} . ${month} . ${year}`;
});

const isQuotesShown = computed(() => {
  const quotes = template.value.hero.quotes;

  return Boolean(quotes.text && quotes.source && props.opened);
});
</script>

<template>
  <section
    id="hero"
    class="relative flex w-full h-safe-min-screen p-section bg-primary-50"
  >
    <!-- Image Assets -->
    <PHeroAssets />
    <PHeroAssetsMobile
      v-if="!md"
    />
    <PHeroAssetsDesktop
      v-if="md"
    />

    <!-- Content -->
    <div
      v-show="props.opened"
      class="relative z-2 flex flex-1 flex-col justify-between gap-18 sm:mx-auto sm:max-w-xl lg:max-w-2xl 2xl:max-w-4xl"
    >
      <div
        class="mt-6 flex size-full flex-1 flex-col items-center justify-start gap-6 text-primary-800 xs:mt-10 sm:mt-12 sm:gap-8 md:gap-12"
      >
        <template v-if="opened">
          <!-- Wordmark Logo -->
          <figure
            class="flex h-auto max-w-50 items-center justify-center sm:max-w-3xs md:max-w-2xs"
            data-aos="zoom-in-up"
            data-aos-delay="200"
            data-aos-duration="700"
            data-aos-anchor="#hero"
          >
            <NuxtImg
              src="/logo.svg"
              alt="Wordmark Logo"
              class="size-full"
              decoding="sync"
              loading="eager"
              fetchpriority="high"
              :preload="{
                fetchPriority: 'high',
              }"
            />
          </figure>

          <h1
            class="font-cursive text-2xl font-bold text-primary-700 xs:text-3xl md:text-4xl lg:text-5xl"
            data-aos="fade-up"
            data-aos-delay="300"
            data-aos-duration="500"
            data-aos-anchor="#hero"
          >
            The Wedding of
          </h1>

          <h2
            class="whitespace-nowrap font-serif text-3xl font-medium xs:text-4xl sm:text-5xl lg:text-6xl xl:text-7xl"
            data-aos="zoom-in-up"
            data-aos-delay="400"
            data-aos-duration="800"
            data-aos-offset="0"
            data-aos-anchor="#hero"
          >
            {{ coupleName }}
          </h2>

          <time
            class="text-xl md:text-2xl lg:text-3xl"
            data-aos="fade-up"
            data-aos-delay="600"
            data-aos-duration="500"
            data-aos-anchor="#hero"
            :datetime="template.general.date"
          >
            {{ formattedDate }}
          </time>
        </template>
      </div>

      <!-- Quotes -->
      <Transition name="hero-quotes">
        <div
          v-if="isQuotesShown"
          class="hero-quotes-expand"
        >
          <div class="min-h-0 overflow-hidden lg:pb-6">
            <blockquote
              class="mx-auto flex max-w-xs flex-col gap-4 text-pretty text-center font-cursive text-2xl font-bold leading-6 text-primary-800 normal-case sm:max-w-120 sm:text-3xl sm:leading-8"
              data-aos="fade"
              data-aos-delay="700"
              data-aos-duration="600"
              data-aos-anchor="#hero"
            >
              <p>
                {{ template.hero.quotes.text }}
              </p>

              <footer>
                <cite class="not-italic text-xl font-bold leading-6 normal-case sm:text-2xl sm:leading-7">
                  {{ template.hero.quotes.source }}
                </cite>
              </footer>
            </blockquote>
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<style lang="css" scoped>
:deep(img.flower-sway.wind-1) {
  --sway-duration: 5.75s;
  --sway-delay: -0.9s;
}

:deep(img.flower-sway.wind-2) {
  --sway-duration: 6.25s;
  --sway-delay: -1.35s;
  --sway-a: -0.6deg;
  --sway-c: 0.3deg;
}

:deep(img.flower-sway.wind-3) {
  --sway-duration: 7s;
  --sway-delay: -0.4s;
  --sway-a: -2.5deg;
  --sway-c: -0.8deg;
}

:deep(img.flower-sway.wind-4) {
  --sway-duration: 6.5s;
  --sway-delay: -1.1s;
}

:deep(img.flower-sway.wind-5) {
  --sway-duration: 7.25s;
  --sway-delay: -0.75s;
  --sway-a: 0.5deg;
  --sway-c: 1deg;
}

.hero-quotes-expand {
  display: grid;
  grid-template-rows: 1fr;
}

.hero-quotes-enter-active,
.hero-quotes-leave-active {
  overflow: hidden;

  transition:
    grid-template-rows 900ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 700ms ease;
}

.hero-quotes-enter-from,
.hero-quotes-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.hero-quotes-enter-to,
.hero-quotes-leave-from {
  grid-template-rows: 1fr;
  opacity: 1;
}

/* @media (prefers-reduced-motion: reduce) {
  .hero-quotes-enter-active,
  .hero-quotes-leave-active {
    transition-duration: 1ms;
  }
} */
</style>
